import React, { forwardRef, useEffect, useMemo, useState } from "react";
import { Box, Chip, Divider, IconButton, MenuItem } from "@mui/material";
import { useTranslation } from "../../../localization/useTranslation";
import { DotLegalAlert, DotLegalHeader, DotLegalLink, DotLegalPrettyScrollbar, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import { EntityType } from "../../Common.types";
import {
    ContentSnippetWithPage,
    DocumentEntityType,
    DocumentSearchResult,
    mapDocumentEntityTypeToEntityType,
    mapDocumentEntityTypeToEntityTypeForTranslation,
} from "./SearchResultList.types.ts";
import { usePlanContext, useFeatureFlags } from "../../../auth/planProvider/PlanProvider.tsx";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import { useSearchNavigation } from "./useSearchNavigation.ts";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useDotLegalFileUploadDataMapping } from "../dotLegalFileUploadTable/DotLegalFileUploadTable.hooks.ts";
import { exhaustiveGuard } from "../../utilities.ts";
import { LegalEntityAuditTabs } from "../../../legalEntity/LegalEntity.types.ts";
import { ContractOverviewTabEnum } from "../../../contract/contractOverview/ContractOverview.types.ts";
import { isPreviewSupported } from "../documentPreview/DocumentPreview.hooks.ts";
import { PreviewableDocument } from "../documentPreview/DocumentPreview.types.ts";
import { isNullOrWhitespace } from "@common/stringOperations.ts";

export interface DocumentSearchResultListProps {
    items: DocumentSearchResult[];
    onItemSelected: () => void;
    onLoadMore?: () => void;
    hasMoreResults?: boolean;
    totalCount?: number;
    onOpenPreview?: (documents: Array<PreviewableDocument>, initialDocumentId: string, initialPage?: number, initialMatchSnippet?: string) => void;
}

export const searchResultMarkStyle = {
    backgroundColor: "#FFF1CA",
    color: "text.secondary",
    padding: "0 2px",
};

const DocumentSearchResultList = forwardRef((props: DocumentSearchResultListProps, ref: any) => {
    const { platformFeatures, addonsFeatures } = usePlanContext();
    const { items, onItemSelected, onLoadMore, hasMoreResults, totalCount, onOpenPreview } = props;
    const { translateEntityType, translateString, translateDate } = useTranslation();
    const urlProvider = useUrlProvider();
    const { downloadDocumentVersionByVersionId } = useDotLegalFileUploadDataMapping();

    const [expandedDocId, setExpandedDocId] = useState<string | null>(null);
    const [hoveredDocId, setHoveredDocId] = useState<string | null>(null);
    const [firstNewItemId, setFirstNewItemId] = useState<string | null>(null);
    const [previousItemsLength, setPreviousItemsLength] = useState(items.length);

    const previewableDocuments: Array<PreviewableDocument> = useMemo(() => {
        return items
            .filter((doc) => doc.fileName && isPreviewSupported(doc.fileName))
            .map((doc) => ({
                documentId: doc.documentId,
                documentVersionId: doc.documentVersionId,
                entityType: mapDocumentEntityTypeToEntityType(doc.entityType),
                entityId: doc.entityId,
                fileName: doc.fileName,
                name: doc.documentTitle,
                entityTitle: doc.entityTitle,
            }));
    }, [items]);

    const { selectedIndex } = useSearchNavigation({
        items,
        onSelect: onDocumentClick,
        ref,
    });

    const handleToggleExpand = (docId: string) => {
        setExpandedDocId((prev) => (prev === docId ? null : docId));
    };

    useEffect(() => {
        if (items.length > previousItemsLength && previousItemsLength > 0) {
            const firstNewItem = items[previousItemsLength];
            if (firstNewItem) {
                setFirstNewItemId(firstNewItem.id);
            }
        }
        setPreviousItemsLength(items.length);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items.length]);

    useEffect(() => {
        if (firstNewItemId) {
            const element = document.getElementById(firstNewItemId);
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
                setFirstNewItemId(null);
            }
        }
    }, [firstNewItemId]);

    const renderUpgradePlan = () => <div>{translateString("upgradePrivacyPlanToday")}</div>;

    const renderShowMoreButton = () => {
        if (!hasMoreResults || !onLoadMore || !totalCount) {
            return null;
        }

        const remainingCount = totalCount - items.length;

        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    paddingY: 2,
                }}
            >
                <DotLegalLink linkColor={"primary"} onClick={onLoadMore}>
                    {translateString("showMore")} ({remainingCount})
                </DotLegalLink>
            </Box>
        );
    };

    async function onDocumentClick(doc: DocumentSearchResult) {
        if (isNullOrWhitespace(doc.fileName)) {
            window.open(doc.content, "_blank", "noopener,noreferrer");
            return;
        }

        if (isPreviewSupported(doc.fileName!) && onOpenPreview) {
            const initialDocumentId = doc.documentVersionId ?? doc.documentId;
            onOpenPreview(previewableDocuments, initialDocumentId);
        } else {
            onItemSelected();
            await downloadDocumentVersionByVersionId(
                doc.documentId,
                doc.documentVersionId,
                doc.fileName!,
                mapDocumentEntityTypeToEntityType(doc.entityType),
                doc.entityId
            );
        }
    }
    function getSnippet(doc: DocumentSearchResult, snippet: ContentSnippetWithPage) {
        return (
            <>
                <Box
                    sx={(theme) => ({
                        color: theme.palette.text.secondary,
                        fontSize: "0.75rem",
                        display: "inline",
                        lineHeight: 1.5,
                        "& mark": {
                            ...searchResultMarkStyle,
                        },
                    })}
                    dangerouslySetInnerHTML={{ __html: snippet.text }}
                />
                {doc.contentSnippets.length > 0 && snippet.pageNumber && (
                    <Chip
                        label={`${translateString("page")} ${snippet.pageNumber}`}
                        sx={(theme) => ({
                            display: "inline",
                            borderRadius: "4px",
                            border: "1px solid",
                            borderColor: theme.palette.primary.main,
                            height: "24px",
                            fontSize: "0.75rem",
                            paddingY: "1px",
                            marginLeft: 1,
                            backgroundColor: "#E3F2FD",
                            color: theme.palette.primary.main,
                            pointerEvents: "none",
                        })}
                    />
                )}
            </>
        );
    }

    function openPreviewAtSnippet(doc: DocumentSearchResult, snippet: ContentSnippetWithPage) {
        if (!onOpenPreview || isNullOrWhitespace(doc.fileName) || !isPreviewSupported(doc.fileName!)) return;
        const initialDocumentId = doc.documentVersionId ?? doc.documentId;
        onOpenPreview(previewableDocuments, initialDocumentId, snippet.pageNumber ?? undefined, snippet.text);
    }

    const snippetBoxStyle = (theme: any) => ({
        borderRadius: 1,
        paddingX: 1,
        marginLeft: -1,
    });

    // Returns undefined when the entity has no addressable page, so the caller renders the title as inert text.
    function getEntityLink(entityType: DocumentEntityType, entityId: string, urlProvider: ReturnType<typeof useUrlProvider>) {
        switch (entityType) {
            case DocumentEntityType.AnnualWheelActivity:
                return urlProvider.getAnnualWheelUrlWithActivityId(entityId);
            case DocumentEntityType.LegalEntity:
                return urlProvider.getLegalEntityUrl(entityId, LegalEntityAuditTabs.Documents);
            case DocumentEntityType.Contract:
                return urlProvider.getContractOverviewUrl(entityId, ContractOverviewTabEnum.Documents);
            case DocumentEntityType.IncidentLog:
                return urlProvider.getIncidentLogOverviewUrl(entityId, 1);
            case DocumentEntityType.Process:
                return urlProvider.getProcessOverviewUrl(entityId, 1);
            case DocumentEntityType.ProcessingActivity:
                return urlProvider.getProcessingActivityOverviewUrl(entityId, 1);
            case DocumentEntityType.Policy:
                return urlProvider.getPolicyUrl(entityId);
            case DocumentEntityType.System:
                return urlProvider.getSystemOverviewUrl(entityId, 1);
            case DocumentEntityType.Task:
                return urlProvider.getTaskManagementTaskDialogUrl(entityId);
            case DocumentEntityType.RiskAssessment:
                // Every risk route is keyed by risk area, which a search result does not carry, so the assessed
                // entity shown as the parent is the only link we can offer for a risk scenario.
                return undefined;
            case DocumentEntityType.ConversationAttachment:
                throw new Error("ConversationAttachment is not surfaced in document search results");
            default:
                exhaustiveGuard(entityType);
        }
    }

    const renderDocumentResults = () => {
        if (items.length === 0) {
            return null;
        }

        return (
            <DotLegalPrettyScrollbar sx={{ ml: -2, mr: -2, maxHeight: "60vh" }}>
                <Box>
                    {items.map((doc, index) => {
                        const isExpanded = expandedDocId === doc.documentVersionId;
                        const isHovered = hoveredDocId === doc.documentVersionId;
                        const hasMultipleSnippets = doc.contentSnippets.length > 1;
                        const entityLink = getEntityLink(doc.entityType, doc.entityId, urlProvider);

                        return (
                            <MenuItem
                                key={`doc-${doc.documentVersionId}-${index}`}
                                id={doc.id}
                                sx={(theme) => ({
                                    paddingX: 2,
                                    paddingY: 0,
                                    whiteSpace: "wrap",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    position: "relative",
                                    backgroundColor: isExpanded ? theme.palette.grey["100"] : "inherit",
                                })}
                                selected={index === selectedIndex}
                                onMouseEnter={() => setHoveredDocId(doc.documentVersionId)}
                                onMouseLeave={() => setHoveredDocId(null)}
                                disableRipple
                            >
                                <Box sx={{ display: "flex", mb: 2, mt: 2, gap: 1, width: "100%", flexDirection: "column", alignItems: "flex-start" }}>
                                    <Box onClick={async () => await onDocumentClick(doc)} sx={{ width: "100%", cursor: "pointer" }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Box sx={{ display: "flex" }}>
                                                <Box
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                    }}
                                                >
                                                    <DotLegalLink linkColor={"primary"} onClick={async () => await onDocumentClick(doc)}>
                                                        <DotLegalHeader marginBottom={0} headerStyle={"small"} color={"secondary"}>
                                                            {doc.documentTitle}
                                                        </DotLegalHeader>
                                                    </DotLegalLink>
                                                </Box>

                                                {doc.versionNumber && (
                                                    <DotLegalTooltip
                                                        text={`${translateString("version")} ${doc.versionNumber} (${translateDate(doc.documentDate)})`}
                                                    >
                                                        <Chip
                                                            label={`v${doc.versionNumber}`}
                                                            sx={(theme) => ({
                                                                borderRadius: "4px",
                                                                height: "20px",
                                                                fontSize: "0.75rem",
                                                                backgroundColor: "#E3F2FD",
                                                                color: theme.palette.primary.main,
                                                                ml: 1,
                                                            })}
                                                        />
                                                    </DotLegalTooltip>
                                                )}
                                            </Box>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                {doc.parentEntityId && (
                                                    <>
                                                        <DotLegalTooltip
                                                            text={translateEntityType(
                                                                EntityType[mapDocumentEntityTypeToEntityTypeForTranslation(doc.parentEntityType!)]
                                                            )}
                                                        >
                                                            <Box
                                                                onClick={(event) => {
                                                                    onItemSelected();
                                                                    event.stopPropagation();
                                                                }}
                                                            >
                                                                <DotLegalLink bold to={getEntityLink(doc.parentEntityType!, doc.parentEntityId, urlProvider)}>
                                                                    <DotLegalHeader marginBottom={0} headerStyle={"small"} color={"primary"}>
                                                                        {doc.parentEntityTitle}
                                                                    </DotLegalHeader>
                                                                </DotLegalLink>
                                                            </Box>
                                                        </DotLegalTooltip>
                                                        <NavigateNextIcon />
                                                    </>
                                                )}
                                                <DotLegalTooltip
                                                    text={translateEntityType(EntityType[mapDocumentEntityTypeToEntityTypeForTranslation(doc.entityType!)])}
                                                >
                                                    <Box
                                                        onClick={(event) => {
                                                            onItemSelected();
                                                            event.stopPropagation();
                                                        }}
                                                    >
                                                        <DotLegalLink bold to={entityLink} disabled={!entityLink}>
                                                            <DotLegalHeader marginBottom={0} headerStyle={"small"} color={"primary"}>
                                                                {doc.entityTitle}
                                                            </DotLegalHeader>
                                                        </DotLegalLink>
                                                    </Box>
                                                </DotLegalTooltip>
                                            </Box>
                                            {doc.contentSnippets.length > 0 && (
                                                <>
                                                    {isExpanded ? (
                                                        <Box
                                                            sx={{
                                                                maxHeight: "300px",
                                                                overflowY: "auto",
                                                                width: "100%",
                                                                marginTop: 0.5,
                                                            }}
                                                        >
                                                            {doc.contentSnippets.map((snippet, snippetIndex) => (
                                                                <Box
                                                                    key={snippetIndex}
                                                                    sx={(theme: any) => ({
                                                                        cursor: "pointer",
                                                                        "&:hover": {
                                                                            backgroundColor: theme.palette.grey["200"],
                                                                        },
                                                                        paddingY: 1.5,
                                                                        borderBottom: "1px solid rgba(0, 0, 0, 0.12);",
                                                                    })}
                                                                >
                                                                    <Box
                                                                        onClick={(event) => {
                                                                            event.stopPropagation();
                                                                            openPreviewAtSnippet(doc, snippet);
                                                                        }}
                                                                        sx={snippetBoxStyle}
                                                                    >
                                                                        {getSnippet(doc, snippet)}
                                                                    </Box>
                                                                    {/*{snippetIndex < doc.contentSnippets.length - 1 && <Divider sx={{ marginY: 1.5 }} />}*/}
                                                                </Box>
                                                            ))}
                                                            {doc.hasMoreOccurrences && (
                                                                <DotLegalAlert
                                                                    severity="info"
                                                                    warningText={translateString("documentSearchMoreThanTenMatches")}
                                                                    marginTop={2}
                                                                    marginBottom={0}
                                                                />
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        <Box
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                openPreviewAtSnippet(doc, doc.contentSnippets[0]);
                                                            }}
                                                            sx={(theme) => ({ mt: 0.5, ...snippetBoxStyle(theme) })}
                                                        >
                                                            {getSnippet(doc, doc.contentSnippets[0])}
                                                        </Box>
                                                    )}
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                    {hasMultipleSnippets && (
                                        <IconButton
                                            size="medium"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleToggleExpand(doc.documentVersionId);
                                            }}
                                            sx={{
                                                position: "absolute",
                                                bottom: 4,
                                                left: "50%",
                                                transform: "translateX(-50%)",
                                                opacity: isHovered ? 1 : 0,
                                                transition: "opacity 0.2s",
                                                backgroundColor: "background.paper",
                                                boxShadow: 1,
                                                "&:hover": {
                                                    backgroundColor: "background.paper",
                                                    filter: "brightness(0.99)",
                                                },
                                            }}
                                        >
                                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                        </IconButton>
                                    )}
                                </Box>
                                {!isExpanded && <Divider sx={{ width: "100%" }} />}
                            </MenuItem>
                        );
                    })}
                    {renderShowMoreButton()}
                </Box>
            </DotLegalPrettyScrollbar>
        );
    };

    return <>{platformFeatures.canAccessRelationsBetweenEntities || addonsFeatures.research ? renderDocumentResults() : renderUpgradePlan()}</>;
});

DocumentSearchResultList.displayName = "DocumentSearchResultList";

export default DocumentSearchResultList;
